import Bugsnag from '@bugsnag/js'
import { createInstance, type Module } from 'i18next'
import Backend from 'i18next-http-backend'
import ICU from 'i18next-icu'
import { initReactI18next } from 'react-i18next'

import { assetUrl } from '@cash-web/shared-util-url'

/**
 * Setup ICU plugin to work correctly with our i18n configuration.
 * Don't let the ICU plugin override i18next's key lookup logic,
 * because we include context in our keys.
 * https://github.com/i18next/i18next-icu/blob/master/src/index.js#L76-L80
 * https://github.com/i18next/i18next/blob/master/src/Translator.js#L469-L498
 */
delete ICU.prototype.addLookupKeys

const i18n = createInstance()

type BackendOptions = typeof Backend.prototype.options
interface i18nOptions {
  locale?: string
  icu?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  plugins?: any[]
  backend?: BackendOptions
}

const detectResolvedLocale = (defaultLocale: string): string => {
  let locale = defaultLocale
  if (typeof window !== 'undefined') {
    if (window?.locale) {
      locale = window?.locale
    }
    // set lang attribute on <html> element
    document.documentElement.lang = locale
  }

  return locale
}

const createLanguageDetector = (defaultLocale: string) =>
  ({
    type: 'languageDetector',
    init: () => undefined,
    cacheUserLanguage: () => undefined,
    detect: () => detectResolvedLocale(defaultLocale),
  }) as Module

export const i18next = (applicationName: string, options: i18nOptions = {}) => {
  const { locale: localeOption, icu = true, plugins } = options
  const locale = localeOption || 'en-US'

  // If already initialized, return the existing instance
  // prevents multiple calls to i18next when developing locally
  if (i18n.isInitialized) {
    return Promise.resolve(i18n)
  }

  let i18nInstance = i18n.use(createLanguageDetector(locale)).use(initReactI18next)

  const thirdPartyModules = plugins || []

  if (icu) {
    thirdPartyModules.push(ICU)
  }

  thirdPartyModules?.forEach(plugin => {
    i18nInstance = i18nInstance.use(plugin)
  })

  // Alias application names if needed for S3 bucket references
  // e.g. account app is named 'account-shell' within cash-web, but the S3 bucket is named 'account', so we need to alias it.
  const appNameToS3Bucket = { 'account-shell': 'account', 'standalone-pages': 'standalone' } as {
    [key: string]: string
  }

  const application = appNameToS3Bucket[applicationName] || applicationName

  // supported namespaces are apps that load translations from S3/CDN for multiple locales.
  // unsupported apps will use fallback behavior (translation key as value).
  const supportedNamespaces = [
    'account-shell',
    'cashtag-p2p',
    'checkout-v2',
    'complaints',
    'lightning',
    'login',
    'marketing-apps',
    'neighborhood-profiles',
    'pools',
    'staff',
    'support',
  ]

  let backendConfig: BackendOptions | undefined
  // Only use HTTP backend on the client side to avoid SSR hangs/timeouts
  // During SSR, i18next will use fallback behavior (translation key as value)
  const isClientSide = typeof window !== 'undefined'
  if (supportedNamespaces.includes(applicationName) && isClientSide) {
    // The Backend plugin is used to load translation files for supported apps.
    i18nInstance = i18nInstance.use(Backend)

    // For supported apps, load translations from S3/CDN with dynamic locale.
    // Return a path with placeholders; the backend interpolates it with lng and ns at load time.
    const lngPlaceholder = icu ? '{lng}' : '{{lng}}'
    const nsPlaceholder = icu ? '{ns}' : '{{ns}}'
    backendConfig = {
      loadPath: () =>
        assetUrl({
          pathPrefix: `apps/i18n-assets/assets/locales/${lngPlaceholder}`,
          filename: `${nsPlaceholder}.json`,
          namespace: application,
        }),
    }
  }

  return i18nInstance.init(
    {
      load: 'currentOnly',
      fallbackLng: 'en-US',
      debug: process.env['DEBUG_I18N'] === 'true',
      defaultNS: applicationName,
      ns: [applicationName],
      keySeparator: false,
      nsSeparator: false,
      contextSeparator: '|░context░|',
      pluralSeparator: '|░plural░|',
      interpolation: {
        escapeValue: false, // not needed for react as it escapes by default
        prefix: icu ? '{' : '{{',
        suffix: icu ? '}' : '}}',
      },
      ...(backendConfig ? { backend: backendConfig } : {}),
      missingKeyHandler: (lngs, ns, key) => {
        const message = `Missing translation in lngs: ${lngs.join(', ')}, namespace: ${ns}, key: ${key}`
        // eslint-disable-next-line no-console
        if (typeof window === 'undefined') console.error(message)

        Bugsnag.notify(new Error(`${applicationName}: ${message}`))
      },
    },
    error => {
      if (error) {
        const message = `i18next failed to initialize: ${error}`

        // eslint-disable-next-line no-console
        console.error(message)
        Bugsnag.notify(new Error(message))
      }
    }
  )
}
